/**
 * Tiny external store for the currently-selected apartment + the building list.
 *
 * Lives outside React so the selection survives navigation between apartment
 * pages and a reload, without needing a Provider high in the tree. Persisted to
 * localStorage. The building list is seeded from the static fallback and then
 * refreshed from the backend (/complexes) on first use, so newly-added
 * buildings appear without a code change. React 16-compatible.
 */
import React from 'react'

import {
  APARTMENTS,
  DEFAULT_APARTMENT_ID,
  fetchApartments
} from 'api/apartment'

const STORAGE_KEY = 'apartment.selectedId'
const listeners = new Set()
const listListeners = new Set()

let apartments = APARTMENTS
let loaded = false
let loading = false

const resolve = (id) =>
  apartments.find((a) => a.id === id) ||
  apartments.find((a) => a.id === DEFAULT_APARTMENT_ID) ||
  apartments[0]

let selected = resolve(
  typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
)

export const getApartments = () => apartments
export const getSelectedApartment = () => selected

const storedId = () =>
  typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null

export const loadApartments = async () => {
  if (loaded || loading) return
  loading = true
  try {
    const list = await fetchApartments()
    if (list && list.length) {
      apartments = list
      loaded = true
      selected = resolve(storedId())
      listListeners.forEach((l) => l(apartments))
      listeners.forEach((l) => l(selected))
    }
  } catch (error) {
    // Leave loaded=false so a later mount retries (e.g. after auth resolves).
    // BUT SAY SO. Swallowing this silently leaves the selector showing the
    // static fallback, which looks like a perfectly normal — just shorter —
    // list of buildings. That is indistinguishable from "those buildings do
    // not exist", and cost real time chasing missing apartments that were in
    // the database all along.
    console.error(
      '[apartments] could not load the building list from /complexes/ — ' +
        'showing the static fallback, which may be out of date.',
      error
    )
  } finally {
    loading = false
  }
}

export const setSelectedApartment = (id) => {
  selected = resolve(id)
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, selected.id)
  }
  listeners.forEach((listener) => listener(selected))
}

// Returns [apartment, setApartmentById]. Re-renders the caller on change.
export const useSelectedApartment = () => {
  const [apartment, setApartment] = React.useState(selected)
  React.useEffect(() => {
    const listener = (next) => setApartment(next)
    listeners.add(listener)
    if (selected !== apartment) setApartment(selected)
    loadApartments()
    return () => listeners.delete(listener)
  }, [apartment])
  return [apartment, setSelectedApartment]
}

// Returns the current building list, refreshing from the backend on mount.
export const useApartments = () => {
  const [list, setList] = React.useState(apartments)
  React.useEffect(() => {
    const listener = (next) => setList([...next])
    listListeners.add(listener)
    loadApartments()
    return () => listListeners.delete(listener)
  }, [])
  return list
}
